import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Wolverhampton\'s AMBER DfT rating mean I can claim?',
    answer: 'Yes — a rating is not a bar to claiming. The Department for Transport rates City of Wolverhampton Council AMBER overall and on condition, GREEN on spend, and AMBER on best practice. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on headline scorecards. The council\'s own report projects 7,500 pothole fills for 2025/26 and admits U-road condition improvements reflect a survey-method change, not completed maintenance.',
  },
  {
    question: 'Why is Wolverhampton projecting 7,500 pothole repairs if 59% of spend is preventative?',
    answer: 'The council plans 59% preventative and 41% reactive maintenance for 2025/26, yet still estimates 7,500 pothole fills — its second-highest count in the published five-year series after 7,973 in 2023/24. Higher preventative percentages have not eliminated reactive workload: fills rose 46% between 2022/23 and 2023/24 despite 56–57% preventative spend in those years. That pattern shows defects still form across the network; your claim turns on your specific road and defect, not aggregate prevention targets.',
  },
  {
    question: 'Wolverhampton says U-road condition improved. Does that weaken my claim?',
    answer: 'Not automatically — and the council qualifies the headline. U-road red-condition share fell from 33% in 2023 to 20% in 2024 after switching to Vaisala AI surveys. The transparency report states explicitly that this reduction "reflects a change in how roads are surveyed rather than any completed maintenance." If your pothole was on a residential U-road — 636km, 82% of the network — cite the methodology caveat alongside photos and prior reports rather than accepting improved percentages at face value.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 635.8km — 82% of Wolverhampton\'s 775.5km carriageway network. These routes are now assessed using Vaisala AI condition surveys aligned to Coarse Visual Inspection standards, replacing earlier visual inspections that updated only 25% of the network annually. At the last published survey, 20% of U-roads were in red condition. If your incident fell between surveys or on a street not yet treated under the 2025/26 resurfacing programme (~26km of preventative carriageway works planned), network-level percentages may not capture your defect.',
  },
  {
    question: 'What does the 46% pothole spike in 2023/24 mean for my case?',
    answer: 'Pothole fills jumped from 5,461 in 2022/23 to 7,973 in 2023/24 — a 46% rise — before falling to 6,431 in 2024/25. That spike happened while preventative spend was 56–57%. It is evidence of a reactive workload surge, not proof your specific defect was unavoidable. Pair the trend with prior reports, photos showing defect age, and the council\'s own admission of "slippage" in planned works when challenging a Section 58 defence.',
  },
  {
    question: 'Wolverhampton has GREEN spend — does that block my claim?',
    answer: 'No. GREEN spend reflects projected 2025/26 capital spend of £4.871m matching the full DfT allocation, with total capital and revenue highways spend rising from £3.89m in 2020/21 to £8.29m projected. Aggregate investment does not prove the individual pothole was known and repaired within inspection intervals. The council also admits delivery "slippage" from consultation delays, deferred schemes and resourcing issues — which raises questions about whether planned maintenance reached your road.',
  },
  {
    question: 'How do I report a pothole to City of Wolverhampton Council?',
    answer: 'Report potholes via the council\'s online form or the Love Clean Streets app at wolverhampton.gov.uk. The council states carriageway defects need to be at least 50mm deep to meet intervention criteria for reporting. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function WolverhamptonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Wolverhampton Pothole Claims | 7,500 Repairs Planned on Amber-Rated Roads | Fixtyer"
        description="City of Wolverhampton Council earns AMBER DfT ratings, projects 7,500 pothole repairs for 2025/26, and admits U-road condition gains reflect a survey change — not maintenance. See what this means for your Section 58 claim."
        canonical="/councils/wolverhampton"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Wolverhampton', url: '/councils/wolverhampton' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="City of Wolverhampton Council"
        councilSlug="wolverhampton"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/wolverhampton"
        headline="Wolverhampton Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Wolverhampton</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            7,500 Pothole Repairs Planned for 2025/26 on an Amber-Rated Network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            City of Wolverhampton Council earns <strong className="text-amber-400">AMBER</strong> DfT scorecards for
            overall performance and condition, <strong className="text-emerald-400">GREEN</strong> on spend — yet its
            own transparency report projects <strong className="text-rose-400">7,500 pothole fills in 2025/26</strong>{' '}
            after a 46% spike to 7,973 in 2023/24. Eighty-two per cent of the network is unclassified residential roads,
            and the council admits improved U-road statistics reflect a survey-method change, not completed maintenance.
            Section 58 still turns on your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">7,973</div>
            <div className="text-white font-medium mt-2">Pothole fills in 2023/24</div>
            <div className="text-brand-300 text-sm mt-1">
              A 46% rise from 5,461 in 2022/23 — the highest in the council&apos;s published five-year series — while
              preventative spend was 57%. The council now projects 7,500 fills for 2025/26 alongside 59% preventative
              maintenance.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            775.5km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from City of Wolverhampton Council&apos;s June 2025 transparency report — where pothole
            claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">775.5km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">635.8km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">82% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">50.4km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">89.3km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Wolverhampton highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">1,280km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">107km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway bridges</td>
                  <td className="px-6 py-4 text-center font-medium">57</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway structures</td>
                  <td className="px-6 py-4 text-center font-medium">83 (1 tunnel, 21 subways, 17 footbridges, 19 culverts, 25 retaining walls)</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Street lighting columns</td>
                  <td className="px-6 py-4 text-center font-medium">27,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="We carry out thousands of repairs to potholes every year with more than 7,500 expected to be repaired in 2025/26. However, to help reduce the number of pothole repairs needed in the long term our approach focuses on preventative maintenance."
            source="City of Wolverhampton Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What AMBER Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER surveys on classified roads from 2024 — Vaisala AI surveys on unclassified roads with a methodology caveat
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> The council transitioned classified roads to annual SCANNER surveys in
              2024 and introduced Vaisala AI condition surveys for unclassified roads. U-road red-condition share fell
              from 33% in 2023 to 20% in 2024 — but the report states this reduction &quot;reflects a change in how
              roads are surveyed rather than any completed maintenance.&quot; Previously, only 25% of the network was
              updated annually under Detailed Visual Inspection, meaning full data was available only every four years.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (89.3km) — surveyed annually via SCANNER</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.9%</td>
                    <td className="px-4 py-2 text-center">18.4%</td>
                    <td className="px-4 py-2 text-center">78.6%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.5%</td>
                    <td className="px-4 py-2 text-center">18.7%</td>
                    <td className="px-4 py-2 text-center">78.8%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">1.9%</td>
                    <td className="px-4 py-2 text-center font-bold">17.0%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">81.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Principal roads show a positive trend — green-rated A-roads rose from 78.6% in 2020 to 81.1% in 2024.
              A-roads are 11.5% of the carriageway network.
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-white border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (50.4km) — surveyed annually via SCANNER</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.34%</td>
                    <td className="px-4 py-2 text-center">22.75%</td>
                    <td className="px-4 py-2 text-center">74.91%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.34%</td>
                    <td className="px-4 py-2 text-center">20.36%</td>
                    <td className="px-4 py-2 text-center">77.30%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">1.76%</td>
                    <td className="px-4 py-2 text-center font-bold">15.14%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">83.10%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C roads have improved on published SCANNER data — but this entire class is just 6.5% of the network.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (635.8km) — where most claims start</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">32%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">35%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">33%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">20%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              The council publishes red-condition percentages only for U-roads — roughly 127km in red category at the
              2024 AI survey. The report explicitly warns the year-on-year drop reflects the survey transition, not
              maintenance completed on the ground.
            </p>
          </div>

          <QuoteCard
            quote="Following this transition to AI-based assessments, the percentage of Unclassified roads in the Red category—those flagged as requiring urgent maintenance—has significantly decreased over the past year. However, it is important to note that this reduction reflects a change in how roads are surveyed rather than any completed maintenance."
            source="City of Wolverhampton Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend — but total highways investment has more than doubled while pothole volumes remain high
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Wolverhampton highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">4,871</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">4,871</td>
                  <td className="px-4 py-3 text-center">3,422</td>
                  <td className="px-4 py-3 text-center text-emerald-400">59%</td>
                  <td className="px-4 py-3 text-center">41%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">4,182</td>
                  <td className="px-4 py-3 text-center">3,786</td>
                  <td className="px-4 py-3 text-center">2,411</td>
                  <td className="px-4 py-3 text-center">61%</td>
                  <td className="px-4 py-3 text-center">39%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">3,325</td>
                  <td className="px-4 py-3 text-center">3,059</td>
                  <td className="px-4 py-3 text-center">2,274</td>
                  <td className="px-4 py-3 text-center">57%</td>
                  <td className="px-4 py-3 text-center">43%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">3,325</td>
                  <td className="px-4 py-3 text-center">3,084</td>
                  <td className="px-4 py-3 text-center">2,379</td>
                  <td className="px-4 py-3 text-center">56%</td>
                  <td className="px-4 py-3 text-center">44%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">1,249</td>
                  <td className="px-4 py-3 text-center">1,249</td>
                  <td className="px-4 py-3 text-center">2,055</td>
                  <td className="px-4 py-3 text-center">38%</td>
                  <td className="px-4 py-3 text-center">62%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">1,811</td>
                  <td className="px-4 py-3 text-center">1,811</td>
                  <td className="px-4 py-3 text-center">2,079</td>
                  <td className="px-4 py-3 text-center">47%</td>
                  <td className="px-4 py-3 text-center">53%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Projected 2025/26 capital spend of £4.871m matches the full DfT allocation. Combined capital and revenue
                spend has risen from £3.89m in 2020/21 to £8.29m projected — a 113% increase — with preventative share
                climbing from 47% to a projected 59%.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                The council admits &quot;slippage&quot; in delivery programmes from consultation impacts, deferred
                schemes and resourcing issues. Spend volume and prevention targets do not prove every defect was caught
                within inspection intervals — especially on 636km of U-roads where the latest condition improvement
                reflects a survey change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Pothole Repair Volumes
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — from the council&apos;s June 2025 transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Wolverhampton estimated potholes filled 2020-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">6,040</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">5,858</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−3.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">5,461</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−9.6%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">7,973</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+32.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">6,431</td>
                  <td className="px-6 py-4 text-center text-amber-600">+6.5%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">7,500</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">+24.2%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="Some slippage occurs between years, this is caused by delays in completing the delivery programme due to a variety of reasons, from impacts of consultation to deferring schemes to complement wider initiatives and occasionally some works are delayed due to external influences and resourcing issues."
              source="City of Wolverhampton Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Roughly 22 patches a day</h3>
              <p className="text-surface-600 text-sm">
                7,973 pothole repairs in 2023/24 works out to roughly 22 patches per day across 775.5km. The council
                projects 7,500 for 2025/26 while planning just ~26km of preventative carriageway treatment — about
                3.4% of the network. Reactive workload remains structurally high.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Planned work */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            What City of Wolverhampton Council says it will deliver this financial year
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">~7km</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Carriageway resurfacing</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">~16km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Surface dressing</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">~3km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Micro asphalt</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">7,500</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Potholes expected</div>
            </div>
          </div>

          <div className="mt-6 bg-white rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              Combined preventative carriageway treatment of ~26km would cover roughly 3.4% of the 775.5km network
              in a single year. The remaining 97% relies on reactive patching, routine safety inspections and
              AI-led condition surveys to catch deterioration — on a network where the council still budgets for 7,500
              pothole fills and admits delivery slippage in planned works.
            </p>
          </div>
        </div>
      </section>

      {/* Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an Amber-Rated Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Wolverhampton invests heavily — but reactive workload and survey caveats create openings
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend matches full DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Classified A and B/C road condition improving on SCANNER data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 59% preventative spend projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual SCANNER surveys on classified roads from 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented innovation — velocity patching, warm mix asphalt, AI surveys
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a prepared Section 58 defence on principal classified roads with strong SCANNER scores.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall, condition and best-practice scorecards from DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 635.8km of U-roads — 82% of network — with AI survey methodology change
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits U-road improvement reflects survey change, not maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 7,973 pothole fills in 2023/24 — 46% spike from prior year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admitted delivery slippage from deferred schemes and resourcing issues
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> ~26km preventative treatment planned — 3.4% of network annually
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and improving classified-road SCANNER data, your claim lives or dies on
              the <strong className="text-white">specific defect and road type</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (Love Clean Streets, council portal) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the AI survey transition caveat is your strongest structural argument</li>
              <li>• Whether planned preventative works reached your street before the council deferred them</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Wolverhampton&apos;s
              own transparency data — including the survey-methodology admission — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to City of Wolverhampton Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when you
            reported it so we can reference it in your pack. The council states carriageway defects must be at least
            50mm deep to meet its reporting intervention threshold.
          </p>
          <a
            href="https://www.wolverhampton.gov.uk/parking-and-roads/roads/report-pothole"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — wolverhampton.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Wolverhampton?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An amber-rated council with high reactive workload demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road survey-methodology argument</li>
                <li>• No prior-report search</li>
                <li>• No slippage admission cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 7,500 projected repairs and 46% spike documented</li>
                <li>✅ U-road AI survey caveat cited</li>
                <li>✅ Delivery slippage admission leveraged</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Wolverhampton</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=wolverhampton"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Wolverhampton Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.wolverhampton.gov.uk/parking-and-roads/local-highway-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              City of Wolverhampton Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
